import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Activity, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { branding } from '@/config/branding';
import { useAuth } from '../AuthProvider';

const accessSubject = encodeURIComponent(`${branding.brandName} access request`);
const accessMailto = `mailto:${branding.contactEmail}?subject=${accessSubject}`;

type Phase = 'signin' | 'new-password';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, confirmNewPassword, error, clearError, isLoading } = useAuth();

  const [phase, setPhase] = useState<Phase>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const reason = searchParams.get('reason');
  const reasonMessage =
    reason === 'session_expired'
      ? 'Your session has expired. Please sign in again.'
      : reason === 'unauthorized'
        ? 'Please sign in to access this page.'
        : '';

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    const result = await signIn({ email, password });
    if (result.kind === 'success') {
      navigate('/dashboard');
    } else if (result.kind === 'new-password-required') {
      setPhase('new-password');
      setPassword('');
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const result = await confirmNewPassword(newPassword);
    if (result.kind === 'success') {
      navigate('/dashboard');
    }
  };

  const errorBanner = (reasonMessage || error || localError) && (
    <div
      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2"
      role="alert"
    >
      <AlertCircle className="h-5 w-5 flex-shrink-0" />
      <span>{reasonMessage || error || localError}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile brand bar */}
      <div className="lg:hidden bg-gray-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6 text-teal-400" />
          <span className="text-lg font-bold text-white">{branding.brandName}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <ShieldCheck className="h-3.5 w-3.5 text-teal-400" />
          <span>Privacy-first</span>
        </div>
      </div>

      {/* Form column */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 py-12 px-6 sm:px-8 lg:px-12 order-1 lg:order-1">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {phase === 'signin' ? 'Sign in' : 'Set a new password'}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              {phase === 'signin'
                ? `Access the ${branding.brandName} research platform`
                : 'Your account requires a new password before you can continue.'}
            </p>
          </div>

          {errorBanner}

          {phase === 'signin' ? (
            <form className="space-y-5" onSubmit={handleSignInSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-teal-600 hover:text-teal-700"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleNewPasswordSubmit}>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    name="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Must include uppercase, lowercase, and a number.
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Repeat the new password"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Saving...
                  </>
                ) : (
                  'Set new password'
                )}
              </button>
            </form>
          )}

          {phase === 'signin' && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-gray-50 px-3 text-gray-400">OR</span>
                </div>
              </div>

              <a
                href={accessMailto}
                className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Request access
              </a>

              <p className="text-xs text-center text-gray-400">
                For research and clinical partners only. The platform is not a diagnostic medical device.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Brand side panel (desktop only) */}
      <div className="hidden lg:flex lg:w-2/5 bg-gray-900 text-gray-100 flex-col items-center justify-center px-12 order-2">
        <div className="max-w-sm space-y-8">
          <div className="flex items-center gap-3">
            <Activity className="h-10 w-10 text-teal-400" />
            <span className="text-2xl font-bold text-white">{branding.brandName}</span>
          </div>

          <p className="text-lg text-gray-300 leading-relaxed">
            Objective motor measurement in the browser, tracked across months and years. Built for clinicians, researchers, and educators working in motor development, physical and occupational therapy, and aging.
          </p>

          <div className="flex items-center gap-2 border border-gray-700 rounded-full px-4 py-2 text-sm text-gray-300">
            <ShieldCheck className="h-4 w-4 text-teal-400 flex-shrink-0" />
            <span>Privacy-first. Raw video stays on the device.</span>
          </div>

          <div className="pt-4 border-t border-gray-800">
            <p className="text-xs text-gray-500">
              A research platform for objective motor assessment across the lifespan.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
